import React, { useEffect, useState, useRef } from "react";
import { AnyBlock, CssBlock as CssBlockType, User } from "../types";
import { styled } from "../stitches.config";
import { useDebounce } from "../utils/useThrottle";
import { remToPixels } from "../ui/Minimap";
import { detect } from "detect-browser";
import { DEFAULT_GRID_RESOLUTION } from "../consts";

export function defaultCSS(user: User) {
  return `/* Write some css! */
.${user?.name} {
  border-width: 1px;
  border-style: solid;
  border-color: black;
  background-color: ${user?.color};
}

.background {

}`;
}

function getFontString(element: HTMLTextAreaElement) {
  let compStyles = window.getComputedStyle(element);
  let font =
    compStyles.getPropertyValue("font-size") +
    " " +
    compStyles.getPropertyValue("font-family");
  return font;
}

export function useManagedTextArea(
  block: AnyBlock,
  updateBlock: (block: Partial<AnyBlock>) => void,
  deleteBlock: (blockId: string) => void,
  activeToggleContainerRef: React.MutableRefObject<HTMLDivElement | null>
) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  let { value, size, id } = block;

  let canvas = useRef<HTMLCanvasElement>();
  let context = useRef<CanvasRenderingContext2D | null>();

  useEffect(() => {
    canvas.current = document.createElement("canvas");
    context.current = canvas.current.getContext("2d");
  }, []);

  function getTextWidth(text: string, element: HTMLTextAreaElement) {
    // re-use canvas context for better performance
    if (!context.current) {
      console.error("context lost");
      context.current = canvas.current!.getContext("2d");
      return 0;
    }
    context.current!.font = getFontString(element);
    let metrics = context.current.measureText(text);
    return metrics.width;
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    e.stopPropagation(); // prevent canvas key commands from conflicting
    switch (e.key) {
      case "Backspace":
        if (block.value === "") deleteBlock(id);
        break;
      case "Escape":
        textAreaRef!.current!.blur();
        break;
      default:
    }
  }

  function onChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    let delta = e.target.value.length - (value?.length || 0);
    let isBigPaste = delta > 100;
    updateBlock({
      value: e.target.value,
    });

    let textHeight = textAreaRef.current?.scrollHeight;
    if (!textHeight) return;

    let newSize = {
      ...size,
    };

    // horizontal expansion only occurs under certain circumstances:
    if (
      !e.target.value.includes("\n") &&
      size.height < 3 &&
      size.width < 12 && // one "post-it note's" width
      !isBigPaste
    ) {
      // "W" is the widest letter, ensuring there is space for the next letter.
      let unflowedTextWidth = getTextWidth(e.target.value + "W", e.target);
      newSize.width = Math.max(
        Math.ceil(
          (unflowedTextWidth + remToPixels(1)) / DEFAULT_GRID_RESOLUTION
        ),
        size.width
      );
    } else {
      const activeToggleContainerHeight = activeToggleContainerRef.current
        ? activeToggleContainerRef.current.clientHeight
        : 0;
      // otherwise expand to fit vertically;
      newSize.height = Math.ceil(
        (textHeight + remToPixels(1) + activeToggleContainerHeight) /
          DEFAULT_GRID_RESOLUTION
      );
    }

    updateBlock({
      size: newSize,
    });
  }

  return { textAreaRef, onKeyDown, onChange };
}

function CssBlock({
  block,
  updateBlock,
  deleteBlock,
  madeBy,
}: {
  block: CssBlockType;
  updateBlock: (block: Partial<AnyBlock>) => void;
  deleteBlock: (blockId: string) => void;
  madeBy?: User;
}) {
  const activeToggleContainerRef = useRef<HTMLDivElement | null>(null);
  const { textAreaRef, onKeyDown, onChange } = useManagedTextArea(
    block,
    updateBlock,
    deleteBlock,
    activeToggleContainerRef
  );
  const [style, setStyle] = useState("");

  function updateStyle(value: string) {
    if (!value) return;
    setStyle(value.replace(/;\n/g, " !important;\n"));
  }

  const updateStyleDebounced = useDebounce(
    updateStyle,
    detect()?.name === "safari" ? 1000 : 0
  );

  useEffect(() => {
    updateStyleDebounced(block.value);
  }, [updateStyleDebounced, block.value]);

  return (
    <Bezel>
      <ActiveToggleContainer ref={activeToggleContainerRef}>
        <label data-cursor={"pointer"}>
          <input
            type="checkbox"
            checked={!block.disabled}
            onChange={(e) => {
              updateBlock({ disabled: !block.disabled });
              // Blur so pressing delete key still deletes the block
              e.target.blur();
            }}
            style={{ marginRight: 6 }}
          />
          active
        </label>
      </ActiveToggleContainer>
      <CodeArea
        spellCheck={false}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onDoubleClick={() => {
          textAreaRef.current!.focus();
        }}
        data-cursor={
          document.activeElement === textAreaRef.current ? "text" : "grab"
        }
        onMouseDown={(e) => {
          // prevent moving/selecting block when it's focused
          if (document.activeElement === e.target) {
            e.stopPropagation();
          }
        }}
        value={block.value ?? ""}
        placeholder={`// Write some css! \n.block {\n    border: 1px solid pink;\n}

  .background {
      background-color: lavender;
  }

  .${madeBy?.name} {
    border-color: ${madeBy?.color};
  }`}
        ref={textAreaRef}
      />
      <style>{block.disabled ? "" : style}</style>
    </Bezel>
  );
}

export default CssBlock;

export const Bezel = styled("div", {
  margin: "$2",
  width: "calc(100% - $4)",
  height: "calc(100% - $4)",
  display: "flex",
  flexDirection: "column",
});

const ActiveToggleContainer = styled("div", {
  display: "flex",
  alignItems: "center",
  padding: "$1",
  paddingBottom: "$2",
});

const CodeArea = styled("textarea", {
  width: "100%",
  height: "100%",
  padding: ".5rem",
  resize: "none",
  color: "$paper10",
  lineHeight: "$5",
  fontSize: "$4",
  fontFamily: "monospace",
  background: "$grey225",
});
